import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Divider,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useMediaQuery,
    useTheme,
    Stack,
    Collapse,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    alpha
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Settings as SettingsIcon,
    AttachMoney as MoneyIcon,
    Refresh as RefreshIcon,
    ExpandMore as ExpandMoreIcon,
    Link as LinkIcon,
    ContentCopy as ContentCopyIcon,
    Close as CloseIcon,
    ViewModule as CardViewIcon,
    TableChart as TableViewIcon
} from '@mui/icons-material';
import { useApiCall } from '../hooks/useApi';
import { api } from '../services/api';
import LoadingPage from '../components/common/LoadingPage';
import { toast } from 'react-toastify';
import AdvancedDataTable from '../components/common/AdvancedDataTable';

const CONCEPT_TYPES = [
    { value: i18n.t('hardcoded.components_ai_aientitycards.vehicle'), label: i18n.t('vehicles.title') },
    { value: 'user', label: i18n.t('users.title') },
    { value: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.sensor'), label: i18n.t('sensors.title') },
    { value: 'alarm', label: i18n.t('hardcoded.pages_billingplanspage.alarmas') },
    { value: 'receiver', label: i18n.t('receivers.title') },
    { value: 'alarm_profile', label: i18n.t('hardcoded.pages_alarmprofilespage.perfiles_de_alerta') },
    { value: 'equipment_tbr', label: i18n.t('hardcoded.pages_billingplanspage.equipos_tbr') },
    { value: 'equipment_pcr', label: i18n.t('hardcoded.pages_billingplanspage.equipos_pcr') },
    { value: 'equipment_otr', label: i18n.t('hardcoded.pages_billingplanspage.equipos_otr') },
    { value: 'feature_calls', label: i18n.t('hardcoded.pages_billingplanspage.funcion_llamadas') },
    { value: 'feature_whatsapp', label: i18n.t('hardcoded.pages_billingplanspage.funcion_whatsapp') },
    { value: 'feature_gps', label: i18n.t('hardcoded.pages_billingplanspage.funcion_gps') },
    { value: 'monthly_fee', label: i18n.t('hardcoded.pages_billingplanspage.cuota_mensual') },
    { value: 'setup_fee', label: i18n.t('hardcoded.pages_billingplanspage.cuota_de_instalacion') },
    { value: i18n.t('hardcoded.pages_billingplanspage.custom'), label: i18n.t('hardcoded.pages_billingplanspage.personalizado') }
];

const getEmptyPlanForm = () => ({
    name: '',
    description: '',
    is_active: true,
    is_default: false,
    free_days_from_registration: 0,
    free_days_from_kit_order: 0,
    free_days_from_equipment_receipt: 0,
    billing_period: 'monthly',
    currency: 'USD'
});

const getEmptyConcept = () => ({
    local_id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    concept_type: 'vehicle',
    concept_name: '',
    concept_description: '',
    is_billable: false,
    is_enabled: true,
    unit_price: 0,
    included_quantity: 0,
    max_quantity: 0,
    ratio_value: null
});

const getConceptLabel = (conceptType) => {
    const found = CONCEPT_TYPES.find((c) => c.value === conceptType);
    return found ? found.label : conceptType;
};

const BillingPlansPage = () => {
  const { t } = useTranslation();
    const theme = useTheme();
    const [planDialog, setPlanDialog] = useState({ open: false, plan: null, mode: t('hardcoded.components_auth_createusermanually.create') });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });
    const [linkDialog, setLinkDialog] = useState({ open: false, plan: null });
    const [linkForm, setLinkForm] = useState({
        billing_plan_id: '',
        link_type: 'client',
        expires_in_days: 7,
        max_uses: 1,
        bill_to: t('hardcoded.pages_billingplanspage.client'),
        show_prices_to_client: true,
        integrator_company_id: '',
        integrator_price: '',
        end_customer_price: '',
        currency: 'USD'
    });
    const [linkLoading, setLinkLoading] = useState(false);
    const [generatedLink, setGeneratedLink] = useState('');
    const [viewMode, setViewMode] = useState(t('hardcoded.pages_billingplanspage.cards'));

    // Check access
    const {
        data: accessCheck,
        loading: accessLoading
    } = useApiCall(() => api.billing.checkAccess());

    // Load plans
    const {
        data: plans,
        loading: plansLoading,
        refetch: refetchPlans
    } = useApiCall(() => api.billing.listPlans());

    const {
        data: companiesResp,
        loading: companiesLoading
    } = useApiCall(() => api.admin.getAllCompanies({ search: '' }));

    const companies = companiesResp?.companies || companiesResp?.data?.companies || [];

    const activePlans = useMemo(() => (plans || []).filter((p) => p.is_active), [plans]);

    const viewModeOptions = [
        { value: t('hardcoded.pages_billingplanspage.cards'), label: t('hardcoded.pages_billingplanspage.tarjetas'), icon: <CardViewIcon /> },
        { value: t('hardcoded.pages_billingplanspage.table'), label: t('hardcoded.pages_billingplanspage.tabla'), icon: <TableViewIcon /> }
    ];

    const handleCreateStandardPlans = async () => {
        try {
            await api.billing.createStandardPlans();
            toast.success(t('hardcoded.pages_billingplanspage.planes_estandar_creados_exitosamente'));
            refetchPlans();
        } catch (error) {
            toast.error(t('hardcoded.pages_billingplanspage.error_al_crear_planes_estandar') + (error.response?.data?.detail || error.message));
        }
    };

    const handleSavePlan = async (planData) => {
        try {
            let targetPlanId = planDialog.plan?.id;

            if (planDialog.mode === 'create') {
                const response = await api.billing.createPlan(planData.plan);
                targetPlanId = response?.data?.id;
                toast.success(t('hardcoded.pages_billingplanspage.plan_creado_exitosamente'));
            } else {
                await api.billing.updatePlan(planDialog.plan.id, planData.plan);
                toast.success(t('hardcoded.pages_billingplanspage.plan_actualizado_exitosamente'));
            }

            if (!targetPlanId) {
                throw new Error(t('hardcoded.pages_billingplanspage.no_se_pudo_determinar_el'));
            }

            const deletedIds = planData.deletedConfigIds || [];
            for (const configId of deletedIds) {
                await api.billing.deleteConfigItem(configId);
            }

            const concepts = planData.concepts || [];
            for (const concept of concepts) {
                const payload = {
                    concept_type: concept.concept_type,
                    concept_name: concept.concept_name,
                    concept_description: concept.concept_description || '',
                    is_billable: !!concept.is_billable,
                    is_enabled: concept.is_enabled ?? true,
                    unit_price: Number(concept.unit_price || 0),
                    included_quantity: Number(concept.included_quantity || 0),
                    max_quantity: Number(concept.max_quantity || 0),
                    ratio_value: concept.ratio_value === '' || concept.ratio_value === null
                        ? null
                        : Number(concept.ratio_value)
                };

                if (!payload.concept_name?.trim()) {
                    continue;
                }

                if (concept.id) {
                    await api.billing.updateConfigItem(concept.id, payload);
                } else {
                    await api.billing.addConfigItem(targetPlanId, payload);
                }
            }

            setPlanDialog({ open: false, plan: null, mode: t('hardcoded.components_auth_createusermanually.create') });
            refetchPlans();
        } catch (error) {
            toast.error(t('hardcoded.pages_billingplanspage.error_al_guardar_plan') + (error.response?.data?.detail || error.message));
        }
    };

    const openLinkDialog = (plan = null) => {
        const selectedPlan = plan || activePlans[0] || plans?.[0] || null;
        const planId = selectedPlan?.id || plans?.[0]?.id || '';

        setLinkDialog({ open: true, plan });
        setGeneratedLink('');
        setLinkForm({
            billing_plan_id: planId,
            link_type: 'client',
            expires_in_days: 7,
            max_uses: 1,
            bill_to: t('hardcoded.pages_billingplanspage.client'),
            show_prices_to_client: true,
            integrator_company_id: '',
            integrator_price: '',
            end_customer_price: '',
            currency: selectedPlan?.currency || 'USD'
        });
    };

    const handleGenerateRegistrationLink = async () => {
        try {
            if (!linkForm.billing_plan_id) {
                toast.error(t('hardcoded.pages_billingplanspage.selecciona_un_plan_de_facturacion'));
                return;
            }

            if (linkForm.link_type === 'integrator_client' && !linkForm.integrator_company_id) {
                toast.error(t('hardcoded.pages_billingplanspage.selecciona_un_integrador_para_esta'));
                return;
            }

            setLinkLoading(true);
            const payload = {
                billing_plan_id: linkForm.billing_plan_id,
                link_type: linkForm.link_type,
                expires_in_days: Number(linkForm.expires_in_days || 7),
                max_uses: Number(linkForm.max_uses || 1),
                bill_to: linkForm.bill_to,
                show_prices_to_client: !!linkForm.show_prices_to_client,
                integrator_company_id: linkForm.link_type === 'integrator_client'
                    ? linkForm.integrator_company_id
                    : null,
                integrator_price: linkForm.integrator_price === '' ? null : Number(linkForm.integrator_price),
                end_customer_price: linkForm.end_customer_price === '' ? null : Number(linkForm.end_customer_price),
                currency: linkForm.currency
            };

            const response = await api.auth.createRegistrationLink(payload);
            const url = response?.data?.url || '';
            setGeneratedLink(url);
            toast.success(t('hardcoded.pages_billingplanspage.liga_generada_correctamente'));
        } catch (error) {
            toast.error(t('hardcoded.pages_billingplanspage.error_al_generar_la_liga') + (error.response?.data?.detail || error.message));
        } finally {
            setLinkLoading(false);
        }
    };

    const handleCopyLink = async () => {
        if (!generatedLink) return;
        try {
            await navigator.clipboard.writeText(generatedLink);
            toast.success(t('hardcoded.pages_billingplanspage.liga_copiada_al_portapapeles'));
        } catch (error) {
            toast.error(t('hardcoded.pages_billingplanspage.no_se_pudo_copiar_la'));
        }
    };

    const handleDeletePlan = async () => {
        try {
            await api.billing.deletePlan(deleteDialog.plan.id);
            toast.success(t('hardcoded.pages_billingplanspage.plan_eliminado_exitosamente'));
            setDeleteDialog({ open: false, plan: null });
            refetchPlans();
        } catch (error) {
            toast.error(t('hardcoded.pages_billingplanspage.error_al_eliminar_plan') + (error.response?.data?.detail || error.message));
        }
    };

    if (accessLoading || plansLoading || companiesLoading) return <LoadingPage />;

    if (!accessCheck?.has_access) {
        return (
            <Container maxWidth={false} disableGutters>
                <Alert severity="error">
                    {t('hardcoded.pages_billinginvoicespage.acceso_denegado_esta_funcionalidad_es')}</Alert>
            </Container>
        );
    }

    const renderPlansCards = () => (
        <Grid container spacing={3}>
            {plans && plans.map((plan) => (
                <Grid item xs={12} md={6} lg={4} key={plan.id}>
                    <Card
                        sx={{
                            height: '100%',
                            border: plan.is_default ? '2px solid' : '1px solid',
                            borderColor: plan.is_default ? 'primary.main' : 'divider',
                            borderRadius: 3,
                            boxShadow: plan.is_default ? '0 8px 24px rgba(0,0,0,0.12)' : '0 4px 12px rgba(0,0,0,0.08)',
                            transition: 'transform .2s ease, box-shadow .2s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 10px 28px rgba(0,0,0,0.14)'
                            }
                        }}
                    >
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={1.5} gap={1}>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                                    {plan.name}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    {plan.is_default && (
                                        <Chip label={t('hardcoded.pages_billingplanspage.predeterminado')} color="primary" size="small" />
                                    )}
                                    <Chip
                                        label={plan.is_active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                                        color={plan.is_active ? 'success' : 'default'}
                                        size="small"
                                    />
                                </Stack>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 46 }}>
                                {plan.description || t('hardcoded.pages_billingplanspage.sin_descripcion')}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        {i18n.t('hardcoded.pages_billinginvoicespage.periodo')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {plan.billing_period?.toUpperCase() || 'MENSUAL'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        {i18n.t('settings.company.currency')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {plan.currency || 'USD'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('hardcoded.pages_billingplanspage.dias_gratis_desde_registro')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {plan.free_days_from_registration || 0} {t('hardcoded.components_alarmprofiles_profiledetail.dias')}</Typography>
                                </Grid>
                            </Grid>

                            <Box mt={2.5} display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
                                <Tooltip title={t('common.edit')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setPlanDialog({ open: true, plan, mode: 'edit' })}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('hardcoded.pages_billingplanspage.generar_liga')}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={!plan.is_active}
                                            onClick={() => openLinkDialog(plan)}
                                        >
                                            <LinkIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={t('audit.actions.DELETE')}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setDeleteDialog({ open: true, plan })}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const renderPlansTable = () => (
        <AdvancedDataTable
            storageKey="billing_plans_table"
            rows={plans || []}
            rowKey="id"
            initialSortBy="name"
            emptyTitle={t('hardcoded.pages_billingplanspage.sin_planes')}
            emptyDescription={t('hardcoded.pages_billingplanspage.no_hay_planes_de_facturacion')}
            columns={[
                {
                    id: 'name',
                    label: t('hardcoded.pages_billingplanspage.nombre_del_plan'),
                    accessor: (row) => row.name || '',
                    filterable: true,
                    render: (row) => <Typography sx={{ fontWeight: 700 }}>{row.name}</Typography>
                },
                {
                    id: 'description',
                    label: t('hardcoded.pages_billingplanspage.descripcion'),
                    accessor: (row) => row.description || '',
                    filterable: true,
                    render: (row) => (
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                            {row.description || t('hardcoded.pages_billingplanspage.sin_descripcion')}
                        </Typography>
                    )
                },
                {
                    id: 'billing_period',
                    label: t('hardcoded.pages_billingplanspage.periodo'),
                    accessor: (row) => row.billing_period || '',
                    filterable: true,
                    render: (row) => (row.billing_period || 'monthly').toUpperCase()
                },
                {
                    id: 'currency',
                    label: t('hardcoded.pages_billingplanspage.moneda'),
                    accessor: (row) => row.currency || 'USD',
                    filterable: true
                },
                {
                    id: 'free_days_from_registration',
                    label: t('hardcoded.pages_billingplanspage.dias_gratis'),
                    accessor: (row) => row.free_days_from_registration ?? 0,
                    filterable: true,
                    render: (row) => t('hardcoded.pages_billingplanspage.rowfree_days_fr_dias', { rowfree_days_fr: row.free_days_from_registration || 0 })
                },
                {
                    id: 'is_active',
                    label: t('hardcoded.pages_billingplanspage.estado'),
                    accessor: (row) => (row.is_active ? 'Activo' : 'Inactivo'),
                    filterable: true,
                    filterType: 'select',
                    filterOptions: [
                        { value: 'Activo', label: t('hardcoded.pages_billingplanspage.activo') },
                        { value: 'Inactivo', label: t('hardcoded.pages_billingplanspage.inactivo') }
                    ],
                    render: (row) => (
                        <Chip
                            size="small"
                            color={row.is_active ? 'success' : 'default'}
                            label={row.is_active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                        />
                    )
                },
                {
                    id: 'is_default',
                    label: t('hardcoded.pages_billingplanspage.predeterminado'),
                    accessor: (row) => (row.is_default ? t('hardcoded.pages_billingplanspage.si') : t('common.no')),
                    filterable: true,
                    filterType: 'select',
                    filterOptions: [
                        { value: 'Sí', label: t('hardcoded.pages_billingplanspage.si') },
                        { value: t('common.no'), label: t('common.no') }
                    ],
                    render: (row) => row.is_default
                        ? <Chip size="small" color="primary" label={t('common.yes')} />
                        : <Chip size="small" variant="outlined" label={t('common.no')} />
                }
            ]}
            rowActions={(plan) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title={t('common.edit')}>
                        <IconButton size="small" onClick={() => setPlanDialog({ open: true, plan, mode: 'edit' })}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('hardcoded.pages_billingplanspage.generar_liga')}>
                        <span>
                            <IconButton
                                size="small"
                                color="primary"
                                disabled={!plan.is_active}
                                onClick={() => openLinkDialog(plan)}
                            >
                                <LinkIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('audit.actions.DELETE')}>
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, plan })}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        />
    );

    return (
        <Container maxWidth={false} disableGutters>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>

                    {t('hardcoded.pages_billingplanspage.planes_de_facturacion')}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={t('audit.actions.UPDATE')}>
                        <IconButton
                            onClick={refetchPlans}
                            size="small"
                            color="primary"
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>

                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                    >
                        {viewModeOptions.map((option) => (
                            <ToggleButton key={option.value} value={option.value}>
                                <Tooltip title={option.label}>
                                    {option.icon}
                                </Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={handleCreateStandardPlans}
                    >
                        {t('hardcoded.pages_billingplanspage.crear_planes_estandar')}</Button>
                    <Button
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={() => openLinkDialog(null)}
                    >
                        {i18n.t('hardcoded.pages_billingplanspage.generar_liga')}</Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setPlanDialog({ open: true, plan: null, mode: 'create' })}
                    >
                        {t('hardcoded.pages_billingplanspage.crear_plan')}</Button>
                </Box>
            </Box>

            {viewMode === 'cards' ? renderPlansCards() : renderPlansTable()}

            {/* Plan Dialog */}
            <PlanDialog
                open={planDialog.open}
                plan={planDialog.plan}
                mode={planDialog.mode}
                onClose={() => setPlanDialog({ open: false, plan: null, mode: 'create' })}
                onSave={handleSavePlan}
            />

            <Dialog
                open={linkDialog.open}
                onClose={() => setLinkDialog({ open: false, plan: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t('hardcoded.pages_billingplanspage.generar_liga_de_registro')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {!linkDialog.plan ? (
                            <FormControl fullWidth>
                                <InputLabel>{t('hardcoded.pages_companiesmanagementpage.plan_de_facturacion')}</InputLabel>
                                <Select
                                    value={linkForm.billing_plan_id}
                                    label={t('hardcoded.pages_companiesmanagementpage.plan_de_facturacion')}
                                    onChange={(e) => {
                                        const selected = (plans || []).find((p) => p.id === e.target.value);
                                        setLinkForm({
                                            ...linkForm,
                                            billing_plan_id: e.target.value,
                                            currency: selected?.currency || linkForm.currency
                                        });
                                    }}
                                >
                                    {(plans || []).map((plan) => (
                                        <MenuItem key={plan.id} value={plan.id}>
                                            {plan.name} ({plan.currency})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Alert severity="info">
                                {t('hardcoded.pages_billingplanspage.plan_de_facturacion')}<strong>{linkDialog.plan.name} ({linkDialog.plan.currency || 'USD'})</strong>
                            </Alert>
                        )}

                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_billingplanspage.tipo_de_liga')}</InputLabel>
                            <Select
                                value={linkForm.link_type}
                                label={t('hardcoded.pages_billingplanspage.tipo_de_liga')}
                                onChange={(e) => setLinkForm({ ...linkForm, link_type: e.target.value })}
                            >
                                <MenuItem value="client">{i18n.t('hardcoded.pages_billingplanspage.cliente_directo')}</MenuItem>
                                <MenuItem value="integrator_client">{t('hardcoded.pages_billingplanspage.cliente_de_integrador')}</MenuItem>
                            </Select>
                        </FormControl>

                        {linkForm.link_type === 'integrator_client' && (
                            <FormControl fullWidth>
                                <InputLabel>{t('hardcoded.pages_companiesmanagementpage.integrador')}</InputLabel>
                                <Select
                                    value={linkForm.integrator_company_id}
                                    label={t('hardcoded.pages_companiesmanagementpage.integrador')}
                                    onChange={(e) => setLinkForm({ ...linkForm, integrator_company_id: e.target.value })}
                                >
                                    {companies.map((company) => (
                                        <MenuItem key={company.id} value={company.id}>
                                            {company.company_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('hardcoded.pages_billingplanspage.expira_en_dias')}
                                    value={linkForm.expires_in_days}
                                    onChange={(e) => setLinkForm({ ...linkForm, expires_in_days: e.target.value })}
                                    inputProps={{ min: 1, max: 365 }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('hardcoded.pages_billingplanspage.usos_maximos')}
                                    value={linkForm.max_uses}
                                    onChange={(e) => setLinkForm({ ...linkForm, max_uses: e.target.value })}
                                    inputProps={{ min: 1, max: 9999 }}
                                />
                            </Grid>
                        </Grid>

                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.pages_billingplanspage.quien_paga')}</InputLabel>
                            <Select
                                value={linkForm.bill_to}
                                label={t('hardcoded.pages_billingplanspage.quien_paga')}
                                onChange={(e) => setLinkForm({ ...linkForm, bill_to: e.target.value })}
                            >
                                <MenuItem value="client">{i18n.t('hardcoded.pages_billingplanspage.cliente_final')}</MenuItem>
                                <MenuItem value="integrator">{t('hardcoded.pages_billingplanspage.integrador')}</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={linkForm.show_prices_to_client}
                                    onChange={(e) => setLinkForm({ ...linkForm, show_prices_to_client: e.target.checked })}
                                />
                            }
                            label={t('hardcoded.pages_billingplanspage.mostrar_precios_al_cliente_final')}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_billingplanspage.precio_para_integrador')}
                            value={linkForm.integrator_price}
                            onChange={(e) => setLinkForm({ ...linkForm, integrator_price: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            type="number"
                            label={t('hardcoded.pages_billingplanspage.precio_para_cliente_final')}
                            value={linkForm.end_customer_price}
                            onChange={(e) => setLinkForm({ ...linkForm, end_customer_price: e.target.value })}
                        />

                        <TextField
                            fullWidth
                            label={t('settings.company.currency')}
                            value={linkForm.currency}
                            onChange={(e) => setLinkForm({ ...linkForm, currency: e.target.value.toUpperCase() })}
                        />

                        {generatedLink && (
                            <TextField
                                fullWidth
                                label={t('hardcoded.pages_billingplanspage.liga_generada')}
                                value={generatedLink}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={handleCopyLink}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLinkDialog({ open: false, plan: null })}>
                        {t('common.close')}</Button>
                    <Button
                        variant="contained"
                        disabled={linkLoading}
                        onClick={handleGenerateRegistrationLink}
                    >
                        {linkLoading ? t('hardcoded.pages_billingplanspage.generando') : t('hardcoded.pages_billingplanspage.generar_liga')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, plan: null })}>
                <DialogTitle>{t('vehicles.actions.confirmDelete')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('hardcoded.pages_billingplanspage.esta_seguro_que_desea_eliminar')}{deleteDialog.plan?.name}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, plan: null })}>
                        {t('common.cancel')}</Button>
                    <Button onClick={handleDeletePlan} color="error" variant="contained">
                        {t('audit.actions.DELETE')}</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

// Plan Dialog Component (unificado con conceptos)
const PlanDialog = ({ open, plan, mode, onClose, onSave }) => {
  const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [formData, setFormData] = useState(getEmptyPlanForm());
    const [concepts, setConcepts] = useState([]);
    const [expandedConcepts, setExpandedConcepts] = useState({});
    const [deletedConfigIds, setDeletedConfigIds] = useState([]);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [conceptsSectionExpanded, setConceptsSectionExpanded] = useState(true);

    useEffect(() => {
        if (!open) return;

        if (plan && mode === 'edit') {
            setFormData({
                name: plan.name || '',
                description: plan.description || '',
                is_active: plan.is_active ?? true,
                is_default: plan.is_default ?? false,
                free_days_from_registration: plan.free_days_from_registration ?? 0,
                free_days_from_kit_order: plan.free_days_from_kit_order ?? 0,
                free_days_from_equipment_receipt: plan.free_days_from_equipment_receipt ?? 0,
                billing_period: plan.billing_period || 'monthly',
                currency: plan.currency || 'USD'
            });
        } else {
            setFormData(getEmptyPlanForm());
        }

        setDeletedConfigIds([]);
        setExpandedConcepts({});
        setConceptsSectionExpanded(true);

        const loadConfig = async () => {
            if (!(plan && mode === 'edit')) {
                setConcepts([]);
                return;
            }

            try {
                setLoadingConfig(true);
                const response = await api.billing.getPlanConfig(plan.id);
                const items = (response?.data || []).map((item) => ({
                    ...item,
                    local_id: item.id
                }));
                setConcepts(items);
            } catch (error) {
                toast.error(t('hardcoded.pages_billingplanspage.error_al_cargar_conceptos') + (error.response?.data?.detail || error.message));
                setConcepts([]);
            } finally {
                setLoadingConfig(false);
            }
        };

        loadConfig();
    }, [open, plan, mode]);

    const setConceptValue = (localId, patch) => {
        setConcepts((prev) => prev.map((item) => (
            item.local_id === localId ? { ...item, ...patch } : item
        )));
    };

    const removeConcept = (concept) => {
        if (concept.id) {
            setDeletedConfigIds((prev) => [...prev, concept.id]);
        }
        setConcepts((prev) => prev.filter((item) => item.local_id !== concept.local_id));
    };

    const addConcept = () => {
        const concept = getEmptyConcept();
        setConcepts((prev) => [...prev, concept]);
        setExpandedConcepts((prev) => ({ ...prev, [concept.local_id]: true }));
    };

    const toggleExpanded = (localId) => {
        setExpandedConcepts((prev) => ({ ...prev, [localId]: !prev[localId] }));
    };

    const handleSubmit = () => {
        if (!formData.name?.trim()) {
            toast.error(t('hardcoded.pages_billingplanspage.el_nombre_del_plan_es'));
            return;
        }

        const invalidConcept = concepts.find((c) => !c.concept_name?.trim());
        if (invalidConcept) {
            toast.error(t('hardcoded.pages_billingplanspage.todos_los_conceptos_deben_tener'));
            return;
        }

        onSave({
            plan: formData,
            concepts,
            deletedConfigIds
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    height: isMobile ? '100%' : '94vh',
                    borderRadius: isMobile ? 0 : 3
                }
            }}
        >
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography variant="h6">
                        {mode === 'create' ? t('hardcoded.pages_billingplanspage.crear_plan_y_configurar_conceptos') : t('hardcoded.pages_billingplanspage.editar_plan_y_conceptos_planname', { planname: plan?.name || '' })}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ backgroundColor: '#f7f8fb', py: 3 }}>
                <Stack spacing={2.5}>
                    <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('hardcoded.pages_billingplanspage.datos_del_plan')}</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('hardcoded.pages_billingplanspage.nombre_del_plan')}
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('audit.table.description')}
                                    multiline
                                    minRows={2}
                                    maxRows={4}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label={t('hardcoded.pages_billingplanspage.periodo_de_facturacion')}
                                    value={formData.billing_period}
                                    onChange={(e) => setFormData({ ...formData, billing_period: e.target.value })}
                                >
                                    <MenuItem value="monthly">{i18n.t('hardcoded.pages_billingplanspage.mensual')}</MenuItem>
                                    <MenuItem value="quarterly">{i18n.t('hardcoded.pages_billingplanspage.trimestral')}</MenuItem>
                                    <MenuItem value="semiannual">{i18n.t('hardcoded.pages_billingplanspage.semestral')}</MenuItem>
                                    <MenuItem value="annual">{i18n.t('hardcoded.pages_billingplanspage.anual')}</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label={t('settings.company.currency')}
                                    value={formData.currency}
                                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                >
                                    <MenuItem value="USD">USD</MenuItem>
                                    <MenuItem value="MXN">MXN</MenuItem>
                                    <MenuItem value="EUR">EUR</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('hardcoded.pages_billingplanspage.dias_gratis_desde_registro')}
                                    value={formData.free_days_from_registration}
                                    onChange={(e) => setFormData({ ...formData, free_days_from_registration: parseInt(e.target.value, 10) || 0 })}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('hardcoded.pages_billingplanspage.dias_gratis_desde_pedido')}
                                    value={formData.free_days_from_kit_order}
                                    onChange={(e) => setFormData({ ...formData, free_days_from_kit_order: parseInt(e.target.value, 10) || 0 })}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('hardcoded.pages_billingplanspage.dias_gratis_desde_recepcion')}
                                    value={formData.free_days_from_equipment_receipt}
                                    onChange={(e) => setFormData({ ...formData, free_days_from_equipment_receipt: parseInt(e.target.value, 10) || 0 })}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        />
                                    }
                                    label={t('hardcoded.pages_billingplanspage.plan_activo')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_default}
                                            onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                                        />
                                    }
                                    label={t('hardcoded.pages_billingplanspage.plan_predeterminado')}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Accordion
                        expanded={conceptsSectionExpanded}
                        onChange={(_, expanded) => setConceptsSectionExpanded(expanded)}
                        disableGutters
                        sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 1, bgcolor: '#fcfcfd' }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                                <Typography variant="h6">{i18n.t('hardcoded.pages_billingplanspage.conceptos_facturables')}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip size="small" variant="outlined" label={t('hardcoded.pages_billingplanspage.length_conceptos', { length: concepts.length })} />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            addConcept();
                                            setConceptsSectionExpanded(true);
                                        }}
                                    >
                                        Agregar concepto
                                    </Button>
                                </Stack>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2.5, bgcolor: '#fff' }}>
                            {loadingConfig ? (
                                <Typography>{i18n.t('hardcoded.pages_billingplanspage.cargando_conceptos')}</Typography>
                            ) : concepts.length === 0 ? (
                                <Alert severity="info">{t('hardcoded.pages_billingplanspage.no_hay_conceptos_todavia_agrega')}</Alert>
                            ) : isMobile ? (
                                <Stack spacing={1.5}>
                                    {concepts.map((concept) => (
                                        <Accordion
                                            key={concept.local_id}
                                            expanded={!!expandedConcepts[concept.local_id]}
                                            onChange={() => toggleExpanded(concept.local_id)}
                                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="subtitle2">
                                                        {concept.concept_name || t('hardcoded.pages_billingplanspage.nuevo_concepto')}
                                                    </Typography>
                                                    <Box display="flex" gap={1} mt={0.5}>
                                                        <Chip size="small" label={getConceptLabel(concept.concept_type)} variant="outlined" />
                                                        <Chip size="small" label={`$${Number(concept.unit_price || 0).toFixed(2)}`} />
                                                        <Chip size="small" color={concept.is_enabled ? 'success' : 'default'} label={concept.is_enabled ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')} />
                                                    </Box>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <ConceptEditor concept={concept} onChange={setConceptValue} onDelete={removeConcept} t={t} />
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 420 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    '& th': {
                                                        fontWeight: 700,
                                                        bgcolor: 'error.main',
                                                        color: '#fff'
                                                    }
                                                }}
                                            >
                                                <TableCell>{t('pages_billingplanspage.table_concept')}</TableCell>
                                                <TableCell>{i18n.t('vehicles.form.type')}</TableCell>
                                                <TableCell align="right">{i18n.t('product.price')}</TableCell>
                                                <TableCell align="center">{i18n.t('hardcoded.pages_billingplanspage.facturable')}</TableCell>
                                                <TableCell align="center">{i18n.t('gps.table.status')}</TableCell>
                                                <TableCell align="right">{t('common.actions')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {concepts.map((concept) => (
                                                <React.Fragment key={concept.local_id}>
                                                    <TableRow hover>
                                                        <TableCell>{concept.concept_name || t('hardcoded.pages_billingplanspage.nuevo_concepto')}</TableCell>
                                                        <TableCell>
                                                            <Chip size="small" variant="outlined" label={getConceptLabel(concept.concept_type)} />
                                                        </TableCell>
                                                        <TableCell align="right">${Number(concept.unit_price || 0).toFixed(2)}</TableCell>
                                                        <TableCell align="center">
                                                            <Chip size="small" color={concept.is_billable ? 'success' : 'default'} label={concept.is_billable ? t('common.yes') : t('common.no')} />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip size="small" color={concept.is_enabled ? 'success' : 'default'} label={concept.is_enabled ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')} />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Button size="small" onClick={() => toggleExpanded(concept.local_id)}>
                                                                {expandedConcepts[concept.local_id] ? t('hardcoded.components_ai_airecommendationspanel.colapsar') : t('common.edit')}
                                                            </Button>
                                                            <IconButton size="small" color="error" onClick={() => removeConcept(concept)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={6} sx={{ py: 0, borderBottom: expandedConcepts[concept.local_id] ? '1px solid' : 'none', borderColor: 'divider' }}>
                                                            <Collapse in={!!expandedConcepts[concept.local_id]}>
                                                                <Box sx={{ p: 2 }}>
                                                                    <ConceptEditor concept={concept} onChange={setConceptValue} onDelete={removeConcept} hideDelete t={t} />
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    p: 1.5
                }}
            >
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained">
                    {mode === 'create' ? t('audit.actions.CREATE') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ConceptEditor = ({ concept, onChange, onDelete, hideDelete = false, t }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
            <TextField
                fullWidth
                select
                label={t('vehicles.form.type')}
                value={concept.concept_type}
                onChange={(e) => onChange(concept.local_id, { concept_type: e.target.value })}
            >
                {CONCEPT_TYPES.map((ct) => (
                    <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>
                ))}
            </TextField>
        </Grid>
        <Grid item xs={12} sm={8}>
            <TextField
                fullWidth
                label={t('hardcoded.pages_billingplanspage.nombre_del_concepto')}
                value={concept.concept_name || ''}
                onChange={(e) => onChange(concept.local_id, { concept_name: e.target.value })}
            />
        </Grid>
        <Grid item xs={12}>
            <TextField
                fullWidth
                label={t('audit.table.description')}
                value={concept.concept_description || ''}
                onChange={(e) => onChange(concept.local_id, { concept_description: e.target.value })}
                multiline
                minRows={2}
            />
        </Grid>
        <Grid item xs={6} sm={3}>
            <TextField
                fullWidth
                type="number"
                label={t('product.price')}
                value={concept.unit_price ?? 0}
                onChange={(e) => onChange(concept.local_id, { unit_price: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: '0.01' }}
            />
        </Grid>
        <Grid item xs={6} sm={3}>
            <TextField
                fullWidth
                type="number"
                label={t('hardcoded.pages_billingplanspage.incluido')}
                value={concept.included_quantity ?? 0}
                onChange={(e) => onChange(concept.local_id, { included_quantity: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ min: 0 }}
            />
        </Grid>
        <Grid item xs={6} sm={3}>
            <TextField
                fullWidth
                type="number"
                label={t('hardcoded.pages_billingplanspage.maximo')}
                value={concept.max_quantity ?? 0}
                onChange={(e) => onChange(concept.local_id, { max_quantity: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ min: 0 }}
                helperText={t('hardcoded.pages_billingplanspage.0_ilimitado')}
            />
        </Grid>
        <Grid item xs={6} sm={3}>
            <TextField
                fullWidth
                type="number"
                label={t('hardcoded.pages_billingplanspage.relacion')}
                value={concept.ratio_value ?? ''}
                onChange={(e) => onChange(concept.local_id, { ratio_value: e.target.value === '' ? null : parseFloat(e.target.value) || null })}
                inputProps={{ min: 0, step: '0.01' }}
            />
        </Grid>
        <Grid item xs={12} sm={6}>
            <FormControlLabel
                control={
                    <Switch
                        checked={!!concept.is_billable}
                        onChange={(e) => onChange(concept.local_id, { is_billable: e.target.checked })}
                    />
                }
                label={t('hardcoded.pages_billingplanspage.facturable')}
            />
        </Grid>
        <Grid item xs={12} sm={6}>
            <FormControlLabel
                control={
                    <Switch
                        checked={concept.is_enabled ?? true}
                        onChange={(e) => onChange(concept.local_id, { is_enabled: e.target.checked })}
                    />
                }
                label={t('common.active')}
            />
        </Grid>
        {!hideDelete && (
            <Grid item xs={12}>
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(concept)}>
                    {t('hardcoded.pages_billingplanspage.eliminar_concepto')}</Button>
            </Grid>
        )}
    </Grid>
);

export default BillingPlansPage;
